'use client';

import { usePathname, useRouter } from 'next/navigation';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

/**
 * Next.js-specific navigation blocker system.
 *
 * This provides similar functionality to react-router-dom's useBlocker but for Next.js App Router.
 * Use this for newly migrated pages that don't need RouterShim compatibility.
 *
 * Usage:
 * 1. Wrap your layout with NavigationBlockerProvider
 * 2. Use useNavigationBlocker(shouldBlock) in components with unsaved changes
 * 3. Use useBlockingRouter() instead of useRouter() for navigation that should respect blockers
 * 4. Render NavigationBlockerModal somewhere in your layout
 */

export type BlockerState = 'unblocked' | 'blocked' | 'proceeding';

export interface BlockerLocation {
  pathname: string;
}

interface BlockerArgs {
  currentLocation: BlockerLocation;
  nextLocation: BlockerLocation;
}

type BlockerFunction = (args: BlockerArgs) => boolean;

// Global blocker store (similar to RouterShim) so RelativeLink can check blockers
const globalBlockers = new Map<string, BlockerFunction>();
let globalTriggerBlock: ((path: string) => void) | null = null;

function registerGlobalBlocker(id: string, shouldBlock: BlockerFunction) {
  globalBlockers.set(id, shouldBlock);
  return () => {
    globalBlockers.delete(id);
  };
}

function checkGlobalBlockers(args: BlockerArgs): boolean {
  for (const shouldBlock of globalBlockers.values()) {
    if (shouldBlock(args)) {
      return true;
    }
  }
  return false;
}

/**
 * Check for active NavigationBlocker blockers and trigger if navigation should be blocked.
 * Returns true if navigation was blocked, false if navigation can proceed.
 * Used by RelativeLink to intercept Link navigation.
 */
export function checkAndTriggerNavigationBlocker(
  currentLocation: BlockerLocation,
  nextLocation: BlockerLocation,
  pendingPath: string,
): boolean {
  if (checkGlobalBlockers({ currentLocation, nextLocation })) {
    globalTriggerBlock?.(pendingPath);
    return true;
  }
  return false;
}

interface NavigationBlockerContextValue {
  // Current blocker state
  blockerState: BlockerState;
  // Proceed with blocked navigation
  proceed: () => void;
  // Cancel blocked navigation
  reset: () => void;
  // The path we're trying to navigate to
  pendingPath: string | null;
}

const NavigationBlockerContext = createContext<NavigationBlockerContextValue | null>(null);

interface NavigationBlockerProviderProps {
  children: ReactNode;
}

export function NavigationBlockerProvider({ children }: NavigationBlockerProviderProps) {
  const router = useRouter();
  const [blockerState, setBlockerState] = useState<BlockerState>('unblocked');
  const [pendingPath, setPendingPath] = useState<string | null>(null);

  // Set up global trigger function
  useEffect(() => {
    globalTriggerBlock = (path: string) => {
      setPendingPath(path);
      setBlockerState('blocked');
    };
    return () => {
      globalTriggerBlock = null;
    };
  }, []);

  const proceed = useCallback(() => {
    if (pendingPath) {
      setBlockerState('proceeding');
      router.push(pendingPath);
      setPendingPath(null);
      setBlockerState('unblocked');
    }
  }, [pendingPath, router]);

  const reset = useCallback(() => {
    setPendingPath(null);
    setBlockerState('unblocked');
  }, []);

  const value = useMemo(
    () => ({
      blockerState,
      proceed,
      reset,
      pendingPath,
    }),
    [blockerState, proceed, reset, pendingPath],
  );

  return (
    <NavigationBlockerContext.Provider value={value}>{children}</NavigationBlockerContext.Provider>
  );
}

function useNavigationBlockerContext() {
  const context = useContext(NavigationBlockerContext);
  if (!context) {
    throw new Error('useNavigationBlocker must be used within a NavigationBlockerProvider');
  }
  return context;
}

/**
 * Hook to register a navigation blocker.
 * Similar to react-router-dom's useBlocker.
 *
 * @param shouldBlock - Boolean or function that returns whether navigation should be blocked
 */
export function useNavigationBlocker(shouldBlock: boolean | BlockerFunction) {
  const { blockerState, proceed, reset } = useNavigationBlockerContext();
  const idRef = useRef(`blocker-${Math.random().toString(36).slice(2)}`);

  // Convert boolean to function
  const shouldBlockFn = useMemo(() => {
    if (typeof shouldBlock === 'boolean') {
      return () => shouldBlock;
    }
    return shouldBlock;
  }, [shouldBlock]);

  // Register the blocker globally
  useEffect(() => {
    return registerGlobalBlocker(idRef.current, shouldBlockFn);
  }, [shouldBlockFn]);

  // Handle browser navigation (back, forward, close tab, refresh)
  useEffect(() => {
    const shouldBlockBeforeUnload = typeof shouldBlock === 'boolean' ? shouldBlock : false;

    if (!shouldBlockBeforeUnload) {
      return;
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      return '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [shouldBlock]);

  return useMemo(
    () => ({
      state: blockerState,
      proceed: blockerState === 'blocked' ? proceed : undefined,
      reset: blockerState === 'blocked' ? reset : undefined,
    }),
    [blockerState, proceed, reset],
  );
}

/**
 * Wrapped useRouter that respects navigation blockers.
 * Use this instead of useRouter() when you want navigation to be blockable.
 */
export function useBlockingRouter() {
  const router = useRouter();
  const pathname = usePathname();

  const push = useCallback(
    (path: string) => {
      const currentLocation = { pathname };
      const nextLocation = { pathname: path.split('?')[0].split('#')[0] };

      if (checkGlobalBlockers({ currentLocation, nextLocation })) {
        globalTriggerBlock?.(path);
        return;
      }

      router.push(path);
    },
    [router, pathname],
  );

  const replace = useCallback(
    (path: string) => {
      const currentLocation = { pathname };
      const nextLocation = { pathname: path.split('?')[0].split('#')[0] };

      if (checkGlobalBlockers({ currentLocation, nextLocation })) {
        globalTriggerBlock?.(path);
        return;
      }

      router.replace(path);
    },
    [router, pathname],
  );

  return useMemo(
    () => ({
      ...router,
      push,
      replace,
    }),
    [router, push, replace],
  );
}

/**
 * Hook to access blocker state for rendering modals.
 */
export function useBlockerState() {
  const { blockerState, proceed, reset } = useNavigationBlockerContext();
  return { state: blockerState, proceed, reset };
}
